import React, { useState, useEffect } from "react";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import {
  useQueryGetExamReports,
  type IExamReportsParams,
} from "@/apis/exam_reports/getExamReports";
import { getPaginationInfo } from "@/types/pagination";
import ExamResultsTable from "./ExamResultsTable";

const EXAM_RESULT_LIST_SETTINGS_KEY = "examResultListSettings";

type SortField =
  | "user_name"
  | "exam_set_title"
  | "exam_type"
  | "total_score"
  | "created_at"
  | null;
type SortOrder = "asc" | "desc";

interface ExamResultListSettings {
  sortField: SortField;
  sortOrder: SortOrder;
}

const defaultSettings: ExamResultListSettings = {
  sortField: null,
  sortOrder: "desc",
};

const ExamResultListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Load initial settings from localStorage once
  const initialSettings = useState(() => {
    try {
      const savedSettings = localStorage.getItem(EXAM_RESULT_LIST_SETTINGS_KEY);
      if (savedSettings) {
        const parsedSettings: ExamResultListSettings =
          JSON.parse(savedSettings);
        return parsedSettings;
      }
    } catch (error) {
      console.error("Failed to load settings from localStorage:", error);
    }
    return defaultSettings;
  })[0];

  // Sorting state with loaded initial values
  const [sortField, setSortField] = useState<SortField>(
    initialSettings.sortField
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    initialSettings.sortOrder
  );

  const [searchParams, setSearchParams] = useState<IExamReportsParams>({
    page: 1,
    limit: 10,
  });

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      const settings: ExamResultListSettings = {
        sortField,
        sortOrder,
      };
      localStorage.setItem(
        EXAM_RESULT_LIST_SETTINGS_KEY,
        JSON.stringify(settings)
      );
    } catch (error) {
      console.error("Failed to save settings to localStorage:", error);
    }
  }, [sortField, sortOrder]);

  // Update search params when sorting changes
  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      sort: sortField || undefined,
      sort_order: sortField ? sortOrder : undefined,
      page: 1, // Reset to first page when sorting changes
    }));
  }, [sortField, sortOrder]);

  // API 호출
  const { data, isLoading, error } = useQueryGetExamReports(searchParams);

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1, // 검색 시 첫 페이지로 이동
    }));
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        // asc → desc
        setSortOrder("desc");
      } else {
        // desc → 정렬 해제
        setSortField(null);
      }
    } else {
      // New field clicked, set to ascending
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Results</Heading>
        <Text className="text-zinc-500">
          View and manage completed exam results from students. Use search to
          find specific results.
        </Text>

        <div className="flex justify-end mt-4">
          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="student name, email, exam set"
                aria-label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Exam Results Table Component */}
        <ExamResultsTable
          examResults={data?.items || []}
          isLoading={isLoading}
          error={error as Error | null}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
        />

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default ExamResultListPage;
